import { Hash, Palette, Users } from "lucide-react";

import type { VanKhiMayMan, VanKhiTuoiHop } from "@/lib/boide-api";
import { cn } from "@/lib/utils";

import { AlmanacGroup, AlmanacRow } from "./van-khi-row";

/** Tên màu BE trả → mã màu để vẽ chấm. Màu lạ thì chỉ hiện chữ. */
const SWATCH: Record<string, string> = {
  trắng: "#f5f5f4",
  bạc: "#c0c4c8",
  "xám ghi": "#8b8f94",
  "xanh lá": "#2e9e4f",
  "xanh lục": "#1f7a3d",
  đen: "#1c1917",
  "xanh dương": "#2563eb",
  "xanh nước biển": "#1e3a8a",
  đỏ: "#dc2626",
  hồng: "#ec4899",
  cam: "#f97316",
  tím: "#7c3aed",
  vàng: "#eab308",
  "nâu đất": "#8b5a2b",
  be: "#e7d8b8",
};

function Swatch({ name, muted = false }: { name: string; muted?: boolean }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border border-border/60 py-0.5 pl-1 pr-2 text-[11px]",
        muted
          ? "text-muted-foreground line-through decoration-muted-foreground/50"
          : "text-foreground/85",
      )}
    >
      {SWATCH[name] ? (
        <span
          className="size-3.5 rounded-full border border-black/10"
          style={{ backgroundColor: SWATCH[name] }}
        />
      ) : null}
      {name}
    </span>
  );
}

/** Chỉ chấm màu, tên màu nằm ở tooltip / aria-label. Màu lạ (không có mã) thì đành hiện chữ. */
function Dot({
  name,
  small = false,
  muted = false,
}: {
  name: string;
  small?: boolean;
  muted?: boolean;
}) {
  const hex = SWATCH[name];
  if (!hex) return <Swatch name={name} muted={muted} />;
  return (
    <span
      role="img"
      aria-label={name}
      title={name}
      className={cn(
        "relative inline-block shrink-0 overflow-hidden rounded-full border border-black/15",
        small ? "size-4" : "size-5",
        muted && "opacity-60",
      )}
      style={{ backgroundColor: hex }}
    >
      {muted ? (
        <span className="absolute left-1/2 top-[-20%] h-[140%] w-px -translate-x-1/2 rotate-45 bg-destructive" />
      ) : null}
    </span>
  );
}

/**
 * Bản gọn nằm TRONG tờ lịch: ba dòng màu / số / tuổi theo khuôn `AlmanacRow`. Lý do chọn
 * màu nằm ở `title` của dòng màu để không chiếm chỗ.
 */
export function VanKhiLuckyInline({
  mayMan,
  tuoiHop,
}: {
  mayMan?: VanKhiMayMan | null;
  tuoiHop?: VanKhiTuoiHop | null;
}) {
  if (!mayMan && !tuoiHop) return null;
  const hop = tuoiHop ? [...tuoiHop.tam_hop, ...(tuoiHop.luc_hop ? [tuoiHop.luc_hop] : [])] : [];
  return (
    <AlmanacGroup>
      {mayMan ? (
        <AlmanacRow icon={<Palette />} label="Màu hợp">
          <div
            className="flex flex-wrap items-center gap-1.5"
            title={`Hành ${mayMan.hanh}: ${mayMan.ly_do}.`}
          >
            {mayMan.mau_hop.map((c) => (
              <Dot key={c} name={c} />
            ))}
            {mayMan.mau_tranh.length ? (
              <>
                <span className="ml-2 text-[11px] text-muted-foreground">tránh</span>
                {mayMan.mau_tranh.map((c) => (
                  <Dot key={c} name={c} small muted />
                ))}
              </>
            ) : null}
          </div>
        </AlmanacRow>
      ) : null}
      {mayMan ? (
        <AlmanacRow icon={<Hash />} label="Số hợp">
          <div className="flex flex-wrap gap-1.5">
            {mayMan.so.map((n) => (
              <span
                key={n}
                className="grid size-6 place-items-center rounded-full border border-gold-soft/60 font-display text-[13px] font-bold text-primary"
              >
                {n}
              </span>
            ))}
          </div>
        </AlmanacRow>
      ) : null}
      {hop.length ? (
        <AlmanacRow icon={<Users />} label="Tuổi hợp">
          <span className="font-semibold text-foreground">{hop.join(" · ")}</span>
          {tuoiHop ? (
            <span className="text-[12px] text-muted-foreground"> · nhường tuổi {tuoiHop.xung}</span>
          ) : null}
        </AlmanacRow>
      ) : null}
      {mayMan ? (
        <p className="text-[10px] leading-4 text-muted-foreground">
          Số theo Hà Đồ — dùng chọn giờ hẹn, số nhà; không dùng cho xổ số, cá cược.
        </p>
      ) : null}
    </AlmanacGroup>
  );
}

/**
 * Màu may mắn · con số hợp · tuổi hợp. Màu/số theo ngũ hành (thông quan khí ngày ↔
 * bản mệnh), số theo Hà Đồ — dùng để chọn trang phục, giờ hẹn, số nhà…, KHÔNG phải
 * số để chơi xổ số. Ẩn hẳn khi bài cũ chưa có dữ liệu.
 */
export function VanKhiLucky({
  mayMan,
  tuoiHop,
  title = "Màu & số may mắn",
  className,
}: {
  mayMan?: VanKhiMayMan | null;
  tuoiHop?: VanKhiTuoiHop | null;
  title?: string;
  className?: string;
}) {
  if (!mayMan && !tuoiHop) return null;
  const hop = tuoiHop ? [...tuoiHop.tam_hop, ...(tuoiHop.luc_hop ? [tuoiHop.luc_hop] : [])] : [];

  return (
    <section className={cn("almanac-panel p-4 sm:p-5", className)}>
      <h3 className="font-display text-base font-bold text-foreground">{title}</h3>
      {mayMan ? (
        <>
          <p className="mt-0.5 text-[11px] leading-5 text-muted-foreground">
            Hành {mayMan.hanh}: {mayMan.ly_do}.
          </p>
          <div className="mt-3 space-y-2.5">
            <div>
              <p className="flex items-center gap-1.5 text-[11px] font-semibold text-element-wood">
                <Palette className="size-3.5" /> Màu nên dùng
              </p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {mayMan.mau_hop.map((c) => (
                  <Swatch key={c} name={c} />
                ))}
              </div>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-destructive">Màu nên hạn chế</p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {mayMan.mau_tranh.map((c) => (
                  <Swatch key={c} name={c} muted />
                ))}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <p className="flex items-center gap-1.5 text-[11px] font-semibold text-primary">
                <Hash className="size-3.5" /> Con số hợp
              </p>
              {mayMan.so.map((n) => (
                <span
                  key={n}
                  className="grid size-7 place-items-center rounded-full border border-gold-soft/60 font-display text-sm font-bold text-primary"
                >
                  {n}
                </span>
              ))}
            </div>
          </div>
        </>
      ) : null}
      {hop.length ? (
        <p className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-border/60 pt-3 text-[12px] text-foreground/85">
          <Users className="size-3.5 text-primary" />
          <span className="font-semibold">Tuổi hợp hôm nay:</span> {hop.join(", ")}
          {tuoiHop ? (
            <span className="text-muted-foreground">· nên nhường nhịn tuổi {tuoiHop.xung}</span>
          ) : null}
        </p>
      ) : null}
      <p className="mt-3 text-[10px] leading-4 text-muted-foreground">
        Con số theo Hà Đồ ngũ hành — dùng khi chọn giờ hẹn, số tầng, số nhà; không dùng cho xổ số,
        cá cược.
      </p>
    </section>
  );
}
